import React, {
	type Dispatch,
	type PropsWithChildren,
	type SetStateAction,
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from 'react';

import { useIntl } from 'react-intl';

import { FlagsProvider } from '@atlaskit/flag/flags-provider';
import { fg } from '@atlaskit/platform-feature-flags/fg';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { media } from '@atlaskit/primitives/responsive/media';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import UFOSegment from '@atlaskit/react-ufo/ufo-segment';
import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { SEARCH_USE_CASE_IDS } from '@atlassian/search-common/search-use-case-id';
import {
	KeyboardHighlightProvider,
	type KeyboardHighlightProviderHandle,
} from '@atlassian/search-dialog/search-keyboard';
import { STATSIG_QUICK_FIND_LAYER } from '@atlassian/search-experiment/constants';
import { useInputIntent } from '@atlassian/search-intent-detection/use-input-intent';

import { ApplicationModes } from '../../common/constants/application-modes';
import { PrimaryProductKeys } from '../../common/constants/primary-product-keys';
import { ProductKeys } from '../../common/constants/product-keys';
import { REFERRER_QUERY_PARAM } from '../../common/constants/query-params';
import { SearchExperience } from '../../common/constants/search-experience';
import {
	AppContextProvider,
	type AppContextProviderProps,
	useAppContext,
} from '../../common/ui/app-context';
import MessagesIntlProvider from '../../common/ui/message-intl-provider/messages-intl-provider';
import type {
	QuickFindFilterPresentation,
	TextfieldProps,
} from '../../common/ui/quick-find/textfield';
import { getInitialQuery } from '../../common/ui/quick-find/textfield/input-skeleton';
import { WidthObserverProvider } from '../../common/ui/width-observer';
import { serveQF3PBannerExperimentWithoutExposure } from '../../common/utils/experiments';
import { serveJiraNINFpsNLQNavigation } from '../../common/utils/experiments/experiment-exports';
import { serveChatConvergenceExperiment } from '../../common/utils/experiments/serve-chat-convergence-experiment';
import type { QueryParams } from '../../common/utils/query-params/types';
import { onQuickFindFiltersToggleButtonClicked } from '../../common/utils/quick-find/events/filter-toggle-button-clicked';
import {
	canContinueRovoJourney,
	canStartRovoJourney,
	normalizeRovoJourneyId,
} from '../../common/utils/quick-find/rovo-journey-id';
import { isOnSearchRoute } from '../../common/utils/url';
import {
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../../controllers/quick-find/analytics';
import { QuickFindHeadlessBannerProvider } from '../../controllers/quick-find/banner-placement/headless-banner-provider';
import { QuickFindResults } from '../../controllers/quick-find/fetch-results';
import { IntentDetectionVitalStatsProvider } from '../../controllers/quick-find/intent-detection-vitalstats-context';
import { QuickFindPerceivedLatencyProvider } from '../../controllers/quick-find/perceived-latency-context';
import { isPromptOfTheDayEnabled } from '../../controllers/quick-find/prompt-of-the-day/is-prompt-of-the-day-enabled';
import { QuickFindPromptOfTheDayProvider } from '../../controllers/quick-find/prompt-of-the-day/prompt-of-the-day-provider';
import { QuickFindPostQueryVitalStatsProvider } from '../../controllers/quick-find/quickfind-post-query-vitalstats-context';
import {
	useApplicationMode,
	useCloudId,
	usePrimaryProduct,
} from '../../controllers/store/bootstrap';
import {
	type BootstrapContainerProps,
	BootstrapStoreProvider as BootstrapProvider,
} from '../../controllers/store/bootstrap/bootstrap-store-provider';
import { useQuickFindActions, useQuickFindOpen } from '../../controllers/store/quick-find';
import { QuickFindStoreProvider } from '../../controllers/store/quick-find/quick-find-store-provider';
import { SearchStoreProvider } from '../../controllers/store/search/search-store-provider';
import { SearchDialogAsync } from './dialog/async';
import { useIntentRouterCleanup } from './dialog/intent-router/use-intent-router-cleanup';
import { useSearchDialogPreload } from './dialog/use-search-dialog-preload';
import type { ExternalInputAnalyticsHandle } from './external-input-analytics';
import { ExternalInputQuerySync } from './external-input-query-sync';
import { FiltersRow } from './filters-row';
import { SearchTextField } from './textfield';
import { useGenerateSearchUrlWithRovoJourneyId } from './use-generate-search-url-with-rovo-journey-id';
import { useLocalQueryWithRovoJourneyId } from './use-local-query-with-rovo-journey-id';
import { useQuickFindOnNavigateWithWarmup } from './use-on-navigate-with-warmup';
import { useCloseManager } from './utils/use-close-manager';
import { usePreQueryDwellTimeAnalytics } from './utils/use-pre-query-dwell-time-analytics';
import { useSyncStates } from './utils/use-sync-states';

const textFieldBoxStyles = xcss({
	width: '100%',
	position: 'relative',
});

const minWidthStyles = xcss({
	left: '50%',
	transform: 'translate(-50%, 0)', // Anchor Quick Find input and dialog to the center of the viewport
	position: 'fixed',
	maxWidth: '780px', // To retain the same width as Quick Search
	zIndex: 'navigation',
	[media.above.lg]: {
		maxWidth: 'unset',
		position: 'relative',
	},
});

/**
 * Lets a host drive Quick Find from an input it owns. Quick Find then renders only the dialog,
 * treats `containerRef` as the trigger for outside-click detection and keyboard navigation, and
 * mirrors `query` into its own state. Behind `rovo_fps_quick_find_external_input`.
 */
export type QuickFindExternalInput = {
	/** Wraps the host input; clicks inside it keep the dialog open and key presses within it drive result navigation. */
	containerRef: React.RefObject<HTMLElement>;
	/** DOM id of the externally controlled results dialog. Generated when omitted. */
	dialogId?: string;
	/** Lets a rich host editor defer Enter to a highlighted result instead of also submitting its query. */
	keyboardHighlightRef?: React.Ref<KeyboardHighlightProviderHandle>;
	/** Publishes the active result id for the host editor aria-activedescendant. */
	onActiveDescendantChange?: (elementId: string | undefined) => void;
	/** Current text of the host input. */
	query: string;
	/** Shared native-input analytics handlers. Available while external-input Quick Find is mounted. */
	analyticsRef?: React.RefObject<ExternalInputAnalyticsHandle | null>;
	/** Invoked when the dialog changes the query itself (e.g. selecting a recent search). */
	onQueryChange?: (query: string) => void;
	/**
	 * Distance in px from the top of the viewport to where the dialog opens. Quick Find assumes it
	 * hangs from the top navigation; hosts that render it further down the page should provide this
	 * so the results section is capped and the dialog ends inside the viewport.
	 */
	topOffset?: number;
};

export type QuickFindComponentProps = {
	open?: boolean;
	setOpen?: (open: boolean) => void;
	postQueryDisabled?: boolean;
	textfieldComponent?: React.ComponentType<TextfieldProps>;
	isSSR?: boolean;
	PlacementPO?: () => JSX.Element;
	showFilters?: boolean;
	showFiltersRow?: boolean;
	searchRefreshStyling?: boolean;
	customPlaceholder?: string;
	onGhostTextGenerated?: (ghostText: string) => void;
	inputRef?: React.RefObject<HTMLInputElement>;
	/** Focus the input once when this changes from false to true. */
	autoFocusInput?: boolean;
	/**
	 * Optional render function for product-specific pre-query content (e.g. connector promo).
	 * Called only during pre-query states. Receives `hasRecentItems` so the rendered
	 * component can adapt its layout.
	 */
	preQueryContent?: (hasRecentItems: boolean) => React.ReactElement;
	/** Predicted advanced-search URL emitted after the typing-debounce window; wire to `prefetchRoute`. Gated on experiment `quick_find_prefetch`. */
	onPrefetch?: (url: string) => void;
	/** Render only the dialog, driven by a host-owned input. See `QuickFindExternalInput`. */
	externalInput?: QuickFindExternalInput;
	/** Suppress promotional and tip banners inside the dialog. */
	hideBanners?: boolean;
};

type QuickFindProps = BootstrapContainerProps & AppContextProviderProps & QuickFindComponentProps;

const QuickFindComponent = ({
	open,
	setOpen,
	postQueryDisabled,
	textfieldComponent,
	isSSR,
	PlacementPO,
	showFilters = true,
	showFiltersRow = false,
	searchRefreshStyling = false,
	customPlaceholder,
	onGhostTextGenerated,
	inputRef,
	autoFocusInput = false,
	preQueryContent,
	onPrefetch,
	externalInput,
	hideBanners,
}: QuickFindComponentProps) => {
	const inputBoxRef = useRef<HTMLDivElement>(null);
	const dialogRef = useRef<HTMLDivElement>(null);
	const { isNav4Enabled, queryParams } = useAppContext();
	const [primaryProduct] = usePrimaryProduct();
	const [cloudId] = useCloudId();
	const applicationMode = useApplicationMode();
	const internalInputRef = useRef<HTMLInputElement>(null);
	const effectiveInputRef = inputRef || internalInputRef;

	const hasFiredChatConvergenceExposure = useRef(false);
	useEffect(() => {
		if (
			primaryProduct !== PrimaryProductKeys.Confluence ||
			hasFiredChatConvergenceExposure.current
		) {
			return;
		}

		hasFiredChatConvergenceExposure.current = true;
		serveChatConvergenceExperiment(primaryProduct, { fireExposure: true });
	}, [primaryProduct]);

	const intl = useIntl();
	const intentRouterEnabled = useMemo(
		() =>
			serveJiraNINFpsNLQNavigation({
				primaryProduct,
				fireLayerExposure: false, // Exposure is fired when a user calls the intent detection API
				locale: intl.locale,
			})?.cohort === 'test',
		[primaryProduct, intl.locale],
	);

	const isExternalInput = !!externalInput && fg('rovo_fps_quick_find_external_input');
	const externalContainerRef = isExternalInput ? externalInput?.containerRef : undefined;
	const externalQuery = isExternalInput ? externalInput?.query : undefined;
	const onExternalQueryChange = isExternalInput ? externalInput?.onQueryChange : undefined;
	// Hosts can supply external-input props before the runtime gate enables that mode.
	// Preserve the existing dialog styling until the external-input path is active.
	const effectiveSearchRefreshStyling =
		externalInput && !fg('rovo_fps_quick_find_external_input') ? false : searchRefreshStyling;

	const [localQuery, setLocalQueryState] = useLocalQueryWithRovoJourneyId(() =>
		externalQuery !== undefined
			? externalQuery
			: getInitialQuery((typeof queryParams.text === 'string' && queryParams.text) || ''),
	);
	const counterfactualIntentDetectionEnabled =
		primaryProduct === PrimaryProductKeys.Confluence &&
		canStartRovoJourney(primaryProduct) &&
		!serveChatConvergenceExperiment(primaryProduct).chatIntentDetectionEnabled;
	const counterfactualIntentResult = useInputIntent({
		input: counterfactualIntentDetectionEnabled ? localQuery : '',
		context: {
			cloudId,
			locale: intl.locale,
			primaryProduct: counterfactualIntentDetectionEnabled
				? PrimaryProductKeys.Confluence
				: undefined,
		},
	});
	const counterfactualIntentDetection = counterfactualIntentDetectionEnabled
		? counterfactualIntentResult
		: undefined;

	// The host owns the input in external-input mode, so mirror its query into local state.
	useEffect(() => {
		if (externalQuery !== undefined) {
			setLocalQueryState(externalQuery);
		}
	}, [externalQuery, setLocalQueryState]);

	const localQueryRef = useRef(localQuery);
	localQueryRef.current = localQuery;

	const setLocalQuery = useCallback<Dispatch<SetStateAction<string>>>(
		(value) => {
			setLocalQueryState(value);
			onExternalQueryChange?.(typeof value === 'function' ? value(localQueryRef.current) : value);
		},
		[setLocalQueryState, onExternalQueryChange],
	);
	const { setQuickFindOpen } = useQuickFindActions();
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();

	const quickFindOpen = useQuickFindOpen();

	const generatedDialogId = useId();
	const dialogId = isExternalInput
		? (externalInput.dialogId ?? generatedDialogId)
		: generatedDialogId;
	const [filtersExpanded, setFiltersExpanded] = useState(false);
	const expandableFiltersEnabled = useMemo(
		() =>
			showFilters &&
			applicationMode === ApplicationModes.Rovo &&
			!textfieldComponent &&
			!isExternalInput &&
			serveChatConvergenceExperiment(primaryProduct).chatIntentDetectionEnabled,
		[applicationMode, isExternalInput, primaryProduct, showFilters, textfieldComponent],
	);

	const filterPresentation = useMemo<QuickFindFilterPresentation | undefined>(
		() =>
			expandableFiltersEnabled
				? {
						regionId: `${dialogId}-filters`,
						isExpanded: filtersExpanded,
						onToggle: () => {
							const isExpanded = !filtersExpanded;

							fireAnalyticsEvent(
								onQuickFindFiltersToggleButtonClicked({
									attributes: commonAttributes,
									isExpanded,
									nonPrivacySafeAttributes,
								}),
							);

							setFiltersExpanded(isExpanded);
						},
					}
				: undefined,
		[
			commonAttributes,
			dialogId,
			expandableFiltersEnabled,
			filtersExpanded,
			fireAnalyticsEvent,
			nonPrivacySafeAttributes,
		],
	);

	const setMinWidthStyles =
		isNav4Enabled &&
		!textfieldComponent && // min-width styles is only supported with the default text field
		!externalContainerRef &&
		quickFindOpen;

	// The first state pair is the source of truth on mount. The host owns the open state in
	// external-input mode, so let it seed the store; otherwise the store seeds the host as before.
	const [syncSourceOpen, setSyncSourceOpen, syncTargetOpen, setSyncTargetOpen] = isExternalInput
		? [open, setOpen, quickFindOpen, setQuickFindOpen]
		: [quickFindOpen, setQuickFindOpen, open, setOpen];
	useSyncStates(syncSourceOpen, setSyncSourceOpen, syncTargetOpen, setSyncTargetOpen);

	useCloseManager({ dialogRef, inputBoxRef: externalContainerRef ?? inputBoxRef });
	usePreQueryDwellTimeAnalytics();

	// Dispose intent detection state when QuickFind unmounts.
	useIntentRouterCleanup();

	const hide3PConnectBannerForChatConvergence =
		serveChatConvergenceExperiment(primaryProduct).chatIntentDetectionEnabled;
	const isPOBannerPlacementforQFEnabled =
		!hideBanners &&
		!hide3PConnectBannerForChatConvergence &&
		serveQF3PBannerExperimentWithoutExposure();

	const [isInteracted, setIsInteracted] = useState(false);
	const isBannerPlacementReady = isInteracted || (!!externalContainerRef && quickFindOpen);

	const onMouseEnter = useCallback(() => setIsInteracted(true), []);
	const onFocus = useCallback(() => setIsInteracted(true), []);

	useSearchDialogPreload(isInteracted || !!externalContainerRef);

	const content = (
		<>
			<QuickFindResults />
			<Box
				xcss={[textFieldBoxStyles, setMinWidthStyles && minWidthStyles]}
				ref={inputBoxRef}
				onMouseEnter={onMouseEnter}
				onFocus={onFocus}
			>
				<KeyboardHighlightProvider
					ref={isExternalInput ? externalInput?.keyboardHighlightRef : undefined}
					onSelectedElementChange={
						isExternalInput ? externalInput?.onActiveDescendantChange : undefined
					}
					listenerNode={
						externalContainerRef ? externalContainerRef.current : effectiveInputRef?.current
					}
				>
					{externalContainerRef ? (
						<ExternalInputQuerySync
							localQuery={localQuery}
							postQueryDisabled={postQueryDisabled}
							analyticsRef={externalInput?.analyticsRef}
						/>
					) : (
						<SearchTextField
							dialogId={dialogId}
							inputRef={effectiveInputRef}
							localQuery={localQuery}
							setLocalQuery={setLocalQuery}
							textfieldComponent={textfieldComponent}
							postQueryDisabled={postQueryDisabled}
							customPlaceholder={customPlaceholder}
							onGhostTextGenerated={onGhostTextGenerated}
							intentRouterEnabled={intentRouterEnabled}
							filterPresentation={filterPresentation}
							onPrefetch={onPrefetch}
							counterfactualIntentDetection={counterfactualIntentDetection}
							autoFocusInput={autoFocusInput}
						/>
					)}
					{!isSSR && (
						<FlagsProvider>
							{quickFindOpen && (
								<WidthObserverProvider>
									<SearchDialogAsync
										dialogId={dialogId}
										dialogRef={dialogRef}
										inputRef={externalContainerRef ? undefined : effectiveInputRef}
										localQuery={localQuery}
										setLocalQuery={setLocalQuery}
										PlacementPO={PlacementPO}
										showFilters={showFilters}
										filterPresentation={filterPresentation}
										searchRefreshStyling={effectiveSearchRefreshStyling}
										preQueryContent={preQueryContent}
										isExternalInput={isExternalInput}
										externalInputTopOffset={isExternalInput ? externalInput?.topOffset : undefined}
										hideBanners={hideBanners}
										counterfactualIntentDetection={counterfactualIntentDetection}
									/>
								</WidthObserverProvider>
							)}
						</FlagsProvider>
					)}
					{showFiltersRow && <FiltersRow />}
				</KeyboardHighlightProvider>
			</Box>
		</>
	);

	const contentWithBanners = isPOBannerPlacementforQFEnabled ? (
		<QuickFindHeadlessBannerProvider isInteracted={isBannerPlacementReady}>
			{content}
		</QuickFindHeadlessBannerProvider>
	) : (
		content
	);

	if (isPromptOfTheDayEnabled(applicationMode, primaryProduct)) {
		return (
			<QuickFindPromptOfTheDayProvider
				isInteracted={isInteracted || quickFindOpen}
				isOpen={quickFindOpen}
			>
				{contentWithBanners}
			</QuickFindPromptOfTheDayProvider>
		);
	}

	return contentWithBanners;
};

export const QuickFindShell = ({
	children,
	queryParams: actualQueryParams,
	callbacks,
	...props
}: PropsWithChildren<QuickFindProps>): React.JSX.Element => {
	const { onSearchCompleted } = callbacks ?? {};
	const customParams = props.primaryProduct?.includes(ProductKeys.Jira)
		? { [REFERRER_QUERY_PARAM]: SearchExperience.QUICK_FIND }
		: {};
	/**
	 * Quick Find does not need to set queryParams, hence using an object
	 * in a state variable as the queryParams object. Any interactions with
	 * the url should be done through onNavigate in the AppContext.
	 */
	const [queryParams, setQueryParams] = useState<QueryParams>({
		text: actualQueryParams?.text,
		...customParams,
	});

	/**
	 * Quick Find only reacts to changes in the text query parameter when the
	 * user is on the search page.
	 */
	const previousTextQueryParam = useRef<string | undefined>(actualQueryParams?.text);
	const initialRovoJourneyId = normalizeRovoJourneyId(actualQueryParams?.rovoJourneyId);
	useEffect(() => {
		if (actualQueryParams?.text === previousTextQueryParam.current) {
			return;
		}

		// If isOnSearchRoute fails and returns undefined, we default to true
		const onSearchRoute = isOnSearchRoute(props.generateSearchUrl) ?? true;
		if (onSearchRoute) {
			setQueryParams((previousQueryParams) => ({
				...previousQueryParams,
				text: actualQueryParams?.text,
			}));
		}

		previousTextQueryParam.current = actualQueryParams?.text;
	}, [actualQueryParams?.text, props.generateSearchUrl]);

	return (
		<BootstrapProvider
			{...props}
			queryParams={queryParams}
			callbacks={{ onSearchCompleted, updateQueryParameters: setQueryParams }}
			statsigLayer={STATSIG_QUICK_FIND_LAYER}
			experience={SearchExperience.QUICK_FIND}
		>
			<SearchStoreProvider
				experience={SearchExperience.QUICK_FIND}
				useCaseId={SEARCH_USE_CASE_IDS.QUICK_FIND}
			>
				<QuickFindStoreProvider
					initialRovoJourneyId={initialRovoJourneyId}
					shouldContinueRovoJourneyId={canContinueRovoJourney(props.primaryProduct)}
					shouldStartRovoJourneyId={canStartRovoJourney(props.primaryProduct)}
				>
					<QuickFindPerceivedLatencyProvider>
						<QuickFindPostQueryVitalStatsProvider>
							<IntentDetectionVitalStatsProvider>
								<QuickFindAppContextProvider {...props} queryParams={queryParams}>
									{children}
								</QuickFindAppContextProvider>
							</IntentDetectionVitalStatsProvider>
						</QuickFindPostQueryVitalStatsProvider>
					</QuickFindPerceivedLatencyProvider>
				</QuickFindStoreProvider>
			</SearchStoreProvider>
		</BootstrapProvider>
	);
};

/**
 * Quick-Find–scoped `AppContextProvider`. Wraps the host-provided `onNavigate`
 * with a warm-up that primes the search-page query cache in the backend when the
 * user navigates to the search page from Quick Find.
 */
const QuickFindAppContextProvider = ({
	children,
	...props
}: PropsWithChildren<AppContextProviderProps>): React.JSX.Element => {
	const generateSearchUrl = useGenerateSearchUrlWithRovoJourneyId(props.generateSearchUrl);
	const wrappedOnNavigate = useQuickFindOnNavigateWithWarmup(props.onNavigate, generateSearchUrl);

	return (
		<AppContextProvider
			{...props}
			{...(generateSearchUrl && { generateSearchUrl })}
			onNavigate={wrappedOnNavigate}
		>
			{children}
		</AppContextProvider>
	);
};

export const QuickFind = ({
	// eslint-disable-next-line no-unused-vars
	children,
	...props
}: PropsWithChildren<QuickFindProps>): React.JSX.Element => {
	return (
		<UFOSegment name="quick-find">
			<MessagesIntlProvider experience={SearchExperience.QUICK_FIND}>
				<RelayEnvironmentProvider>
					<QuickFindShell {...props}>
						<QuickFindComponent {...props} />
					</QuickFindShell>
				</RelayEnvironmentProvider>
			</MessagesIntlProvider>
		</UFOSegment>
	);
};
